import {
  ClockCircleOutlined,
  EllipsisOutlined,
  LeftOutlined,
  CheckCircleFilled,
  QuestionCircleOutlined,
  ClockCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Row,
  Tag,
  Typography,
  Card,
  message,
  Col,
} from "antd";
import dayjs from "dayjs";
import { useLoaderData, useNavigate } from "react-router-dom";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { ReactComponent as CopyIcon } from "assets/images/copy-icon.svg";
import { ReactComponent as ContentDueIcon } from "assets/images/content-due-icon.svg";
import { getGoogleMapsUrl } from "helpers/google-maps-utils";
import { Booking } from "interfaces/booking";
import { BookingStatus } from "enums/BookingStatus";
import { calculateTimeLeft } from "pages/collabs/Collabs";
import { chooseRescheduleDate } from "services/SauceService";
import { isMobile } from "react-device-detect";
import { onClickCopy } from "helpers/copy-helper";
import { useState } from "react";
import { restaurantCheckin } from "helpers/checkin-helper";
import "../collabs/Collabs.scss";

const labelByStatus = (status: BookingStatus, applicationDate: string) => {
  switch (status) {
    case BookingStatus.PendingConfirmation:
    case BookingStatus.RequestedCancellation:
    case BookingStatus.PendingRestaurantApproval:
      return "Pending";
    case BookingStatus.BookingConfirmed:
      const currentDateTime = dayjs();
      if (dayjs(applicationDate).isBefore(currentDateTime)) {
        return "Active";
      } else {
        return "Upcoming";
      }
    case BookingStatus.WaitingContent:
    case BookingStatus.PendingContentReview:
      return "Active";
    case BookingStatus.Complete:
      return "Completed";
    case BookingStatus.Rejected:
    case BookingStatus.Canceled:
    case BookingStatus.Expired:
      return "Canceled";
    case BookingStatus.RequestedChange:
      return "Action Pending";
    default:
      return "Unknown";
  }
};

const colorTagByStatus = (status: BookingStatus) => {
  switch (status) {
    case BookingStatus.PendingConfirmation:
    case BookingStatus.RequestedChange:
    case BookingStatus.RequestedCancellation:
    case BookingStatus.PendingRestaurantApproval:
    case BookingStatus.BookingConfirmed:
    case BookingStatus.WaitingContent:
    case BookingStatus.PendingContentReview:
      return "gray";
    case BookingStatus.Complete:
      return "green";
    case BookingStatus.Rejected:
    case BookingStatus.Canceled:
    case BookingStatus.Expired:
      return "error";
    default:
      return "gray";
  }
};

const canConfirmAttendance = (
  status: BookingStatus,
  applicationDate: string,
  attendanceConfirmation: boolean
) => {
  const currentDateTime = dayjs();
  const targetDate = dayjs(applicationDate).startOf("day");
  const diff = targetDate.diff(currentDateTime, "day");

  if (
    status === BookingStatus.BookingConfirmed &&
    diff === 0 &&
    !attendanceConfirmation
  ) {
    return true;
  }
  return false;
};

const RequestChangeButtons: React.FC<{ booking: Booking }> = (props) => {
  const { booking } = props;
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<any>(
    booking.preferredDates.length === 1 ? booking.preferredDates[0] : null
  );
  const [loading, setLoading] = useState<boolean>(false);

  const onClickAccept = async () => {
    setLoading(true);
    const params = {
      collabId: booking.collabId,
      bookingId: booking.bookingId,
      slotTimeId: selectedDate._id,
    };
    try {
      await chooseRescheduleDate(params);
      navigate("/");
    } catch (e) {
      message.error("Error accepting the new date");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="book-preferred-dates">
      <div style={{ display: "flex", flexDirection: "column", flex: "100%" }}>
        <h3 className="title">
          <b>{booking.venue.name} </b>
          would like to accept you but at a different time than you initially
          offered.
          {booking.preferredDates?.length > 1
            ? ` Please select one of the available slots below to reschedule your booking`
            : ` Please click accept to confirm`}
        </h3>
        {booking.preferredDates?.map((date) => (
          <div
            className={`book-date ${
              date._id === selectedDate?._id ? "selected" : ""
            }`}
          >
            <span onClick={() => setSelectedDate(date)}>
              {`${dayjs(date.date).format("dddd, MMM DD, ")} ${date.hour}`}
            </span>
          </div>
        ))}
      </div>
      <Button
        size="large"
        type="primary"
        shape="round"
        disabled={!selectedDate}
        onClick={onClickAccept}
        loading={loading}
        style={{ flex: 1 }}
      >
        Accept
      </Button>
    </div>
  );
};

const BookDetail: React.FC = () => {
  const { booking } = useLoaderData() as { booking: Booking };
  const navigate = useNavigate();

  const isToday = dayjs(booking.applicationDate?.date || 0).isSame(
    dayjs(),
    "day"
  );
  const isPast = dayjs(booking.applicationDate?.date || 0).isBefore(
    dayjs(),
    "day"
  );
  const laDateCancelationTime = dayjs(booking.applicationDate?.date || 0)
    .tz("America/Los_Angeles")
    .set("hour", 18)
    .set("minute", 0)
    .format();

  const contentDue =
    booking.status === BookingStatus.WaitingContent ||
    (booking.status === BookingStatus.BookingConfirmed && isPast);

  const onClickChangeDateTime = async () => {
    navigate(`/book/reschedule`, {
      state: { booking },
    });
  };

  const onClickCancelCollab = async () => {
    navigate("/book/cancel", { state: { booking } });
  };

  const onConfirm = () => {
    navigate("/confirm", { state: { collab: booking } });
  };

  const onCheckin = async () => {
    await restaurantCheckin(booking, navigate);
  };

  const items = [
    {
      label: "Change Date/Time",
      className: "regular-menu-item",
      key: "change-date-time",
      onClick: onClickChangeDateTime,
    },
    {
      type: "divider",
    },
    {
      label: "Cancel Collab",
      className: "danger-menu-item",
      key: "cancel-collab",
      onClick: onClickCancelCollab,
    },
  ];

  const Buttons = () => {
    return (
      <>
        {canConfirmAttendance(
          booking.status,
          booking.applicationDate?.date!,
          booking.attendanceConfirmation
        ) && (
          <>
            <Button
              type="primary"
              onClick={onConfirm}
              className="button-with-content"
              style={{ flex: "auto", width: "100%" }}
            >
              <span className="main">I confirm</span>
              <span>{calculateTimeLeft({ date: laDateCancelationTime })}</span>
            </Button>
          </>
        )}

        {booking.status === BookingStatus.BookingConfirmed &&
          booking.attendanceConfirmation &&
          isToday && (
            <Button
              type="primary"
              onClick={onCheckin}
              className="button-with-content"
              style={{ flex: "auto", width: "100%" }}
            >
              <span className="main">Check in</span>
              <span>To be greeted by venue's staff</span>
            </Button>
          )}

        {booking.status === BookingStatus.WaitingContent && isToday && (
          <Button
            type="primary"
            size="large"
            shape="round"
            style={{ width: "100%" }}
            onClick={() =>
              navigate("/certificate", {
                state: { booking },
              })
            }
          >
            Show voucher
          </Button>
        )}

        {(booking.status === BookingStatus.WaitingContent ||
          (booking.status === BookingStatus.BookingConfirmed && isPast)) && (
          <Button
            size="large"
            type="primary"
            shape="round"
            onClick={() => navigate("/book/submit-content", { state: booking })}
            style={{ flex: 1 }}
          >
            Submit content
          </Button>
        )}

        {booking.status === BookingStatus.RequestedChange && (
          <RequestChangeButtons booking={booking} />
        )}
      </>
    );
  };

  if (contentDue) {
    return (
      <div
        style={{ padding: 20, marginBottom: 80 }}
        className="book-detail collab-detail"
      >
        <Row align="middle" style={{ marginBottom: 12 }}>
          <LeftOutlined
            style={{ cursor: "pointer", fontSize: 18, marginRight: 8 }}
            onClick={() => navigate(-1)}
          />
        </Row>
        <div>
          <Typography.Title level={3} style={{ margin: 0, flex: 1 }}>
            {booking.venue.name}
          </Typography.Title>

          <Tag
            className={`tag error body-16-semi-bold-inter`}
            style={{ borderRadius: 8, marginBottom: 12 }}
          >
            Content Due on{" "}
            {dayjs(booking.applicationDate?.date).format("MMM DD")}{" "}
            {booking.applicationDate?.hour} PST
          </Tag>
          {booking.applicationDate?.date && (
            <Row style={{ gap: 8 }}>
              <ClockCircleOutlined style={{ fontSize: 18 }} />
              {`${dayjs
                .utc(booking.applicationDate?.date)
                .format("dddd, MMM DD, ")} ${booking.applicationDate?.hour}`}
            </Row>
          )}
          <Row style={{ marginTop: 12, gap: 6 }}>
            <LocationIcon />
            <a
              href={getGoogleMapsUrl(booking.venue)}
              target="_blank"
              rel="noreferrer"
              className="ellipsis"
              style={{ width: 300 }}
            >
              {booking.venue.addressDisplay ?? booking.venue.address}
            </a>
          </Row>

          <Card
            variant="borderless"
            style={{
              borderRadius: 32,

              marginTop: 16,
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div
                style={{
                  backgroundColor: "#E5E7EB",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <CheckCircleFilled style={{ fontSize: 32, color: "#3DC86F" }} />
              </div>
              <Typography.Title
                level={4}
                style={{ marginTop: 12, marginBottom: 4 }}
                className="heading-3-22-semi-bold"
              >
                Hope you enjoyed your experience!
              </Typography.Title>
              <Typography.Paragraph
                style={{ color: "#1D2023" }}
                className="body-16-regular-poppins"
              >
                It's time to submit your content to complete this collab.
              </Typography.Paragraph>
            </div>

            <div style={{ marginTop: 30 }}>
              <Row
                align="top"
                gutter={16}
                style={{ marginBottom: 20 }}
                wrap={false}
              >
                <Col>
                  <ContentDueIcon style={{ marginTop: 4 }} />
                </Col>
                <Col flex="auto">
                  <Typography.Text
                    style={{ display: "block" }}
                    className="body-16-semi-bold-inter"
                  >
                    Submit by {dayjs(booking.submitDueDate).format("MMM DD")}{" "}
                    with instructions completed
                  </Typography.Text>

                  <Typography.Text
                    type="secondary"
                    className="body-16-regular"
                    style={{ color: "#1D2023" }}
                  >
                    Your ranking will go up.{" "}
                    {booking.compensationCash > 0 &&
                      "If you have a comp, it will be paid out"}
                  </Typography.Text>
                </Col>
              </Row>

              <Row
                align="top"
                gutter={16}
                style={{ marginBottom: 20 }}
                wrap={false}
              >
                <Col>
                  <ClockCircleFilled
                    style={{
                      color: "#F5222D",
                      fontSize: 20,
                      marginTop: 4,
                    }}
                  />
                </Col>
                <Col flex="auto">
                  <Typography.Text
                    className="body-16-semi-bold-inter"
                    style={{ display: "block" }}
                  >
                    Late Submission
                  </Typography.Text>

                  <Typography.Text
                    type="secondary"
                    className="body-16-regular"
                    style={{ color: "#1D2023" }}
                  >
                    Submitting after the deadline will negatively impact your
                    ranking.
                  </Typography.Text>
                </Col>
              </Row>

              <Row align="top" gutter={16} wrap={false}>
                <Col>
                  <CloseCircleFilled
                    style={{
                      color: "#F5222D",
                      fontSize: 20,
                      marginTop: 4,
                    }}
                  />
                </Col>
                <Col flex="auto">
                  <Typography.Text
                    className="body-16-semi-bold-inter"
                    style={{ display: "block", color: "#FF354A" }}
                  >
                    No Submission at all
                  </Typography.Text>

                  <Typography.Text
                    type="secondary"
                    className="body-16-regular"
                    style={{ color: "#1D2023" }}
                  >
                    Not submitting any content will result in a{" "}
                    <Typography.Text strong style={{ color: "#FF354A" }}>
                      significant drop in your ranking
                    </Typography.Text>{" "}
                    and may lead to being{" "}
                    <Typography.Text strong>banned</Typography.Text> from the
                    network.
                  </Typography.Text>
                </Col>
              </Row>
            </div>
          </Card>

          <Button
            type="primary"
            size="large"
            block
            style={{ borderRadius: 24, marginBottom: 12, marginTop: 32 }}
            onClick={() => navigate("/book/submit-content", { state: booking })}
          >
            Submit Content
          </Button>
          <Button
            type="text"
            size="large"
            block
            style={{ borderRadius: 24 }}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>

          <div style={{ marginTop: 24 }} className="disclaimer-box">
            <Typography.Title level={5} style={{ margin: 0 }}>
              <QuestionCircleOutlined style={{ color: "#1E3EFF" }} /> What
              happens next?
            </Typography.Title>
            <Typography.Paragraph
              type="secondary"
              style={{ lineHeight: 1.6, margin: 0 }}
            >
              After you submit,{" "}
              <Typography.Text strong>{booking.venue.name}</Typography.Text>{" "}
              reviews content, you'll be notified once it's approved and your
              rewards are processed.
            </Typography.Paragraph>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }} className="book-detail collab-detail">
      <Row justify="space-between">
        <LeftOutlined
          style={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        />
        <Dropdown menu={{ items: items as any }} trigger={["click"]}>
          <EllipsisOutlined className="bigger-svg" />
        </Dropdown>
      </Row>
      <div style={{ overflow: "auto" }}>
        <Typography.Title level={1} style={{ marginTop: 0 }}>
          {booking.venue.name}
        </Typography.Title>
        <Tag
          className={`tag ${colorTagByStatus(booking.status)}`}
          style={{ borderRadius: 8, marginBottom: 12 }}
        >
          {labelByStatus(booking.status, booking?.applicationDate?.date!)}
        </Tag>
        {booking.applicationDate?.date && (
          <Row style={{ gap: 8 }}>
            <ClockCircleOutlined style={{ fontSize: 18 }} />
            {`${dayjs
              .utc(booking.applicationDate?.date)
              .format("dddd, MMM DD, ")} ${booking.applicationDate?.hour}`}
          </Row>
        )}
        <Row style={{ marginTop: 12, gap: 6 }}>
          <LocationIcon />
          <a
            href={getGoogleMapsUrl(booking.venue)}
            target="_blank"
            rel="noreferrer"
            className="ellipsis"
            style={{ width: 300 }}
          >
            {booking.venue.addressDisplay ?? booking.venue.address}
          </a>
        </Row>
        <Row style={{ gap: 8, marginTop: 12 }}>
          {booking.guestAllowed && (
            <div className="box-perk">
              🍴
              <div className="value">
                <span>Guests</span>
                <span>
                  <b>+1</b>
                </span>
              </div>
            </div>
          )}
          {booking.compensationFood > 0 && (
            <div className="box-perk">
              🍔
              <div className="value">
                <span>Food</span>
                <span>
                  <b>${booking.compensationFood}</b>
                </span>
              </div>
            </div>
          )}
          {booking.compensationCash > 0 && (
            <div className="box-perk">
              💸
              <div className="value">
                <span>Comp</span>
                <span>
                  <b>${booking.compensationCash}</b>
                </span>
              </div>
            </div>
          )}
        </Row>

        <Row>
          <div className="briefing" style={{ margin: "20px 0 100px 0" }}>
            <span className="title">Brief:</span>
            <Typography.Paragraph>
              {booking.collabDetails?.focusPoints}
            </Typography.Paragraph>
            <span className="title">Deliverables:</span>
            <ul>
              {booking?.deliverables?.map((item) => (
                <li className="circle checkmark" key={item}>
                  {item}
                </li>
              ))}
            </ul>
            {booking?.collabDetails?.promotionOfferSummary && (
              <>
                <span className="title">
                  Mention this offer to your followers:
                </span>
                <Typography.Paragraph style={{ display: "flex", gap: 6 }}>
                  {booking?.collabDetails?.promotionOfferSummary}
                </Typography.Paragraph>
              </>
            )}
            {booking?.collabDetails?.promotionActionDm &&
              booking?.instagramAccount && (
                <>
                  <span className="title">
                    To receive the offer they should DM @
                    {booking?.instagramAccount}:
                  </span>
                  <Typography.Paragraph
                    style={{ display: "flex", gap: 6 }}
                    onClick={() =>
                      onClickCopy(booking?.collabDetails?.promotionActionDm)
                    }
                  >
                    {booking?.collabDetails?.promotionActionDm}{" "}
                    <CopyIcon style={{ cursor: "pointer" }} />
                  </Typography.Paragraph>
                </>
              )}

            {booking?.collabDetails?.promotionSwipeLink && (
              <>
                <span className="title">
                  Add this swipe-up link to your story:
                </span>
                <Typography.Paragraph
                  style={{ display: "flex", gap: 6 }}
                  onClick={() =>
                    onClickCopy(booking?.collabDetails?.promotionSwipeLink)
                  }
                >
                  {booking?.collabDetails?.promotionSwipeLink}{" "}
                  <CopyIcon style={{ cursor: "pointer" }} />
                </Typography.Paragraph>
              </>
            )}
            {booking?.collabDetails?.promotionCaptionLink && (
              <>
                <span className="title">
                  Use this caption for the swipe-up:
                </span>
                <Typography.Paragraph
                  style={{ display: "flex", gap: 6 }}
                  onClick={() =>
                    onClickCopy(booking?.collabDetails?.promotionCaptionLink)
                  }
                >
                  {booking?.collabDetails?.promotionCaptionLink}{" "}
                  <CopyIcon style={{ cursor: "pointer" }} />
                </Typography.Paragraph>
              </>
            )}
          </div>
        </Row>
        <Row
          style={{
            position: isMobile ? "fixed" : "inherit",
            width: isMobile ? "calc(100% - 24px)" : "100%",
            left: isMobile ? 12 : "50%",
            bottom: isMobile ? 100 : 0,
          }}
        >
          <Buttons />
        </Row>
      </div>
    </div>
  );
};

export default BookDetail;
